<script setup lang="ts">

import {ref} from "vue";
import {API_URL, getTimezone, parseEloCredentialsError} from "../ts/utils";
import {$t, getSelectedLocale} from "../ts/translations";
import PasswordChangeForm from "./PasswordChangeForm.vue";

const props = defineProps(['username'])

const waitForResponse = ref(false)
const timezone = ref(getTimezone());

const username = ref(props.username ?? '')
const password = ref('')
const errorMessage = ref('')

const changePassword = ref(null) // 'MUST_CHANGE', 'SHOULD_CHANGE' or 'EXPIRED'
const passwordRules = ref(null)

const emit = defineEmits(['success'])

async function  eloLogin() {
  errorMessage.value = ''

  waitForResponse.value = true
  const res = await fetch(API_URL + "/elo-login", {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      username: username.value,
      password: password.value,
      timezone: timezone.value,
      locale: getSelectedLocale()
    })
  })
  waitForResponse.value = false

  if (res.ok) {
    let json = await res.json()
    if (json.changePassword === 'NO_CHANGE') {
      // To resume the redirection flow to the client (possibly code exchange flow)
      emit('success')
    } else {
      // Currently, a failed login is still passed as OK
      // because failures cannot provide a JSON response with more details
      // since the IX REST Plugin removes any payloads for error responses.
      changePassword.value = json.changePassword;
      passwordRules.value = json.passwordRules;
    }
  } else {
    console.warn(`Failed to sign-in (${res.statusText})`)
    errorMessage.value = await parseEloCredentialsError(res);
  }
}

function onPasswordChanged() {
  // To resume the redirection flow to the client (possibly code exchange flow)
  emit('success')
}

function skipChangePassword() {
  console.log('Password change skipped')
  // To resume the redirection flow to the client (possibly code exchange flow)
  emit('success')
}

async function loginAndChangePassword(newPassword :string) {
  return fetch(API_URL + "/elo-login-with-new-password", {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      username: username.value,
      password: password.value,
      timezone: timezone.value,
      locale: getSelectedLocale(),
      newPassword
    })
  })
}
</script>

<template>
  <b-loading is-full-page v-model="waitForResponse"></b-loading>

  <template v-if="!changePassword">
    <form id="eloLoginForm" @submit.prevent="eloLogin()">

        <b-field :label="$t('username_or_email')" label-for="username">
          <b-input v-model="username" id="username" name="username" type="text" autofocus></b-input>
        </b-field>
        <b-field :label="$t('password')" class="my-4" label-for="password">
          <b-input v-model="password" id="password" name="password" autocomplete="off" type="password" password-reveal></b-input>
        </b-field>

        <div class="error-message" v-if="errorMessage">
          <b-icon icon="notification-error" class="mx-1"></b-icon>
          <span data-test-id="error-message">{{errorMessage}}</span>
        </div>

        <slot><!-- EAUTH2-114: this is used by the rescue page to insert an additional switch! --></slot>

        <b-button :disabled="!username || !password" native-type="submit" type="is-dark" class="mt-5" expanded @click.prevent="eloLogin()" data-test-id="button-sign_in">{{$t('sign_in')}}</b-button>
    </form>
  </template>

  <template v-if="changePassword && changePassword != 'NO_CHANGE'">
    <PasswordChangeForm
        :oldPassword="password"
        :passwordRules="passwordRules"
        :changePasswordFunction="changePassword == 'MUST_CHANGE' || changePassword == 'EXPIRED' ? loginAndChangePassword : null"
        @password-changed="onPasswordChanged()"
    />
    <p class="mt-5" v-if="changePassword == 'SHOULD_CHANGE'">
      <!-- EAUTH2-312: make it focusable per keyboard and act as button to be more screenreader friendly -->
      <a @click.prevent="skipChangePassword()" data-test-id="button-change_password_skip" tabindex="0" role="button">{{$t('change_password_skip')}}</a>
    </p>
  </template>
</template>
